import React, { useCallback } from 'react'
import { ExternalLink } from '@labrav/react-components'
import { AccordionSectionProps } from '@labrav/react-components'
import { ClassNameMap } from '@mui/material/styles'
import {
  LoadingTypes,
  type ComponentLoadingState,
} from '../../../modules/loading/reducer'
import { isLoading as Loading } from '../../../utils/loadingState'
import {
  helpDrawerUrl,
  SaasProductSectionLinks,
} from '../../../utils/constants'
import {
  faCircleUser,
  faBriefcase,
  faKey,
  faFile,
  faGift,
  faChartLine,
  faEnvelope,
  faNetworkWired,
  faStar,
  faCirclePlay,
  faLink,
} from '@fortawesome/pro-regular-svg-icons'
import {
  AccordionDescription,
  AccordionHeading,
} from '../AccordionComponents/AccordionComponents'
import { useDispatch, useSelector } from 'react-redux'
import {
  getMilestonesApiData,
  patchMilestonesApiData,
  type MilestonesDispatch,
} from '../MilestonesAction/MilestonesAction'
export const getSaasProductSections = (
  classes: ClassNameMap<string>,
  saasProductsMiestonesData: Record<string, boolean>,
  setSaasProductsData: React.Dispatch<React.SetStateAction<unknown>>,
  partnerId: string
): AccordionSectionProps[] => {
  const dispatch = useDispatch<MilestonesDispatch>()
  const { isLoading } = useSelector(
    (state: { loading: ComponentLoadingState }) => ({
      isLoading: Loading(state.loading)(LoadingTypes.GENERAL),
    })
  )
  const handleButtonClick = useCallback(
    (keyName: string) => async () => {
      if (partnerId) {
        await dispatch(patchMilestonesApiData(partnerId, keyName))
        await dispatch(getMilestonesApiData(partnerId, setSaasProductsData))
      }
    },
    [partnerId]
  )

  return [
    {
      key: 'awsAccountCreated',
      heading: (
        <AccordionHeading
          icon={faCircleUser}
          text="Create an account on the AWS Marketplace Management Portal"
          completed={saasProductsMiestonesData['awsAccountCreated']}
        />
      ),
      description: (
        <AccordionDescription
          descriptionText="Access and use the AWS Marketplace Management Portal. This is the tool that you use to register as a seller and manage the products that you sell in AWS Marketplace. For more information,"
          link={SaasProductSectionLinks.awsAccountCreated}
          linkText="please check this article"
        />
      ),
    },
    {
      key: 'sellerRegistrationCompleted',
      heading: (
        <AccordionHeading
          icon={faBriefcase}
          text="Register as a seller"
          completed={saasProductsMiestonesData['sellerRegistrationCompleted']}
        />
      ),
      description: (
        <AccordionDescription
          descriptionText={
            <>
              Register as a seller{' '}
              <a
                href={
                  SaasProductSectionLinks.sellerRegistrationCompleted
                    .sellerRegistrationHeader
                }
                target="_blank"
                className={classes.linkText}
              >
                via this link
              </a>
              , and submit your tax and banking information. For more
              information,
            </>
          }
          link={
            SaasProductSectionLinks.sellerRegistrationCompleted
              .sellerRegistrationSubHeader
          }
          linkText="please check this article"
        />
      ),
    },
    {
      key: 'cloudSettingsSetupCompleted',
      heading: (
        <AccordionHeading
          icon={faKey}
          text="Set up your Cloud settings to give Labra permissions to manage
              your listings"
          completed={saasProductsMiestonesData['cloudSettingsSetupCompleted']}
        />
      ),
      description: (
        <AccordionDescription
          descriptionText="Connect your cloud account with Labra during onboarding to give
              request permissions to Labra to manage your listing. For more
              information,"
          link={helpDrawerUrl}
          linkText="please check this article"
        />
      ),
    },
    {
      key: 'firstProductListingCreated',
      heading: (
        <AccordionHeading
          icon={faFile}
          text="Create your first listing"
          completed={saasProductsMiestonesData['firstProductListingCreated']}
        />
      ),
      description: (
        <AccordionDescription
          descriptionText="Open the Product Listings page inside the Labra application and click
          on ‘Add new listing’ and start creating your first product."
        />
      ),
    },
    {
      key: 'firstPrivateOfferCreated',
      heading: (
        <AccordionHeading
          icon={faEnvelope}
          text="Create your first private offer"
          completed={saasProductsMiestonesData['firstPrivateOfferCreated']}
        />
      ),
      description: (
        <AccordionDescription
          descriptionText="Open the ‘Offers & Authorization’ page inside the platform and click
          on ‘Create’ -> ‘Create new private offer’ to start creating your
          first private offer."
          linkText="please check this article"
          link={SaasProductSectionLinks.firstPrivateOfferCreated}
        />
      ),
    },
    {
      key: 'freeTrialEnabled',
      heading: (
        <AccordionHeading
          icon={faGift}
          text="Enable Free Trial in your listing"
          completed={saasProductsMiestonesData['freeTrialEnabled']}
        />
      ),
      description: (
        <AccordionDescription
          descriptionText="Enable a Free Trial on your listing which enables your buyers to try your software before they buy it. Free trials are limited to a certain amount of free usage, or for a specific amount of time. For more information,"
          linkText="please check this article"
          link={SaasProductSectionLinks.freeTrialEnabled}
          loading={isLoading}
          showButton={!saasProductsMiestonesData['freeTrialEnabled']}
          buttonClickHandler={handleButtonClick('freeTrialEnabled')}
        />
      ),
    },
    {
      key: 'vendorInsightsEnabled',
      heading: (
        <AccordionHeading
          icon={faChartLine}
          text="Enable AWS Marketplace Vendor Insights for your listing"
          completed={saasProductsMiestonesData['vendorInsightsEnabled']}
        />
      ),
      description: (
        <AccordionDescription
          descriptionText="Enable AWS Marketplace Vendor Insights feature which simplifies
            software risk assessments for your buyers. This ensures them
            becoming more likely to procure software that they trust and that
            meets their industry standards. For more information,"
          loading={isLoading}
          link={SaasProductSectionLinks.vendorInsights}
          linkText="please check this article"
          showButton={!saasProductsMiestonesData['vendorInsightsEnabled']}
          buttonClickHandler={handleButtonClick('vendorInsightsEnabled')}
        />
      ),
    },
    {
      key: 'apnPartnerRegistrationCompleted',
      heading: (
        <AccordionHeading
          icon={faNetworkWired}
          text={
            <>
              <div className={classes.headerText}>
                Access{' '}
                <ExternalLink
                  href={
                    SaasProductSectionLinks.apnPartnerRegistrationCompleted
                      .apnPartnerRegistrationCompletedHeader
                  }
                >
                  AWS Partner Central
                </ExternalLink>{' '}
                and register your company to become an AWS Partner Network (APN)
                partner.
              </div>
            </>
          }
          completed={
            saasProductsMiestonesData['apnPartnerRegistrationCompleted']
          }
        />
      ),
      description: (
        <AccordionDescription
          descriptionText="Check whether or not you're an AWS Partner Network (APN) partner.
            Please go to AWS Partner Central to register or login into your
            account. For more information on how to register,"
          loading={isLoading}
          showButton={
            !saasProductsMiestonesData['apnPartnerRegistrationCompleted']
          }
          link={
            SaasProductSectionLinks.apnPartnerRegistrationCompleted
              .apnPartnerRegistrationCompletedSubHeader
          }
          linkText="please check this article"
          buttonClickHandler={handleButtonClick(
            'apnPartnerRegistrationCompleted'
          )}
        />
      ),
    },
    {
      key: 'aceEligible',
      heading: (
        <AccordionHeading
          icon={faStar}
          text={
            <>
              <div className={classes.headerText}>
                Become{' '}
                <ExternalLink href={SaasProductSectionLinks.aceEligible}>
                  APN Customer Engagements (ACE)
                </ExternalLink>{' '}
                eligible.
              </div>
            </>
          }
          completed={saasProductsMiestonesData['aceEligible']}
        />
      ),
      description: (
        <AccordionDescription
          descriptionText="Once you have successfully been onboarded as a AWS Partner Network
            (APN) partner, the next step is to become APN Customer Engagements
            (ACE) eligible."
          loading={isLoading}
          showButton={!saasProductsMiestonesData['aceEligible']}
          buttonClickHandler={handleButtonClick('aceEligible')}
        />
      ),
    },
    {
      key: 'poButtonAndGuidedDemoEnabled',
      heading: (
        <AccordionHeading
          icon={faCirclePlay}
          text="Enable ‘Request a Private Offer’ & ‘Guided demonstration’ feature
              for your listing"
          completed={saasProductsMiestonesData['poButtonAndGuidedDemoEnabled']}
        />
      ),
      description: (
        <AccordionDescription
          descriptionText={
            <>
              <p>
                1. If you're an AWS Partner Network (APN) partner who's eligible
                for APN Customer Engagements (ACE) you can choose to provide
                your AWS buyers with:
              </p>
              <ol type="a">
                <li>
                  Unique or enterprise use cases to request a private offer for
                  your product directly from the product detail page inside your
                  listing
                </li>
                <li>Guided demonstrations on AWS Marketplace</li>
              </ol>
              <p>
                2. If you'd like to provide this option to buyers, contact your
                AWS representative to determine further eligibility.
              </p>
            </>
          }
          loading={isLoading}
          showButton={
            !saasProductsMiestonesData['poButtonAndGuidedDemoEnabled']
          }
          buttonClickHandler={handleButtonClick('poButtonAndGuidedDemoEnabled')}
        />
      ),
    },
    {
      key: 'ammpApnAccountsConnected',
      heading: (
        <AccordionHeading
          icon={faLink}
          text="Connect your AWS Partner Central account to an AWS Marketplace
              account and map user permissions across portals."
          completed={saasProductsMiestonesData['ammpApnAccountsConnected']}
        />
      ),
      description: (
        <AccordionDescription
          descriptionText="Connect your AWS Partner Central account to an AWS Marketplace
            account and map user permissions across portals, which will allow
            you to seamlessly access both accounts through single sign-on
            access, and enables product-to-offering linking across platforms.
            Enjoy benefits such as increased operational efficiencies and
            streamlined listing experience and more. For more information,"
          loading={isLoading}
          showButton={!saasProductsMiestonesData['ammpApnAccountsConnected']}
          link={SaasProductSectionLinks.ammpApnAccountsConnected}
          linkText="please check this article"
          buttonClickHandler={handleButtonClick('ammpApnAccountsConnected')}
        />
      ),
    },
  ]
}
